import { ClickAwayListener } from '@mui/material';
import ChatPanelRuntime from 'components/Chat/ChatPanelRuntime';
import { CompactRightVariant } from 'components/Chat/ChatPromptSuggestions';
import { useDialog } from 'contexts/dialog';
import useInstructorHugoChat from 'hooks/chat/useInstructorHugoChat';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { IconButton, Loading, Stack, styled } from 'shell/components';
import { ChatLayout } from 'utils/chat/chatConstants';
import useHugoPanelAnimation from '../../../hooks/chat/useHugoPanelAnimation';
import useHugoThreadTransitions from '../../../hooks/chat/useHugoThreadTransitions';
import { HugoPanelTransitionMs, LauncherInitialTopPx } from './constants';
import HugoLauncherControl from './HugoLauncherControl';
import HugoPanelFrame, { HugoPanelShell } from './HugoPanelFrame';
import HugoThreadSidebar from './HugoThreadSidebar';

const noop = () => {};

const InstructorHugoChat = ({ onFullScreenChange = noop }) => {
    const location = useLocation();
    const { t } = useTranslation();
    const {
        dialog: { open: isDialogOpen },
        setDialog,
        closeDialog,
    } = useDialog();

    const [chatLayout, setChatLayout] = useState(ChatLayout.DEFAULT);
    const [launcherVerticalPosition, setLauncherVerticalPosition] = useState(LauncherInitialTopPx);
    const [isThreadSidebarExpanded, setIsThreadSidebarExpanded] = useState(false);
    const { closePanel, isOpen, isPanelMounted, isPanelVisible, openPanel } = useHugoPanelAnimation({
        transitionMs: HugoPanelTransitionMs,
    });

    const {
        buildOutgoingRequest,
        chat,
        isBootstrapping,
        isChatRouteSynced,
        onChatRequestStateChange,
        onDeleteThread,
        onRefreshThreads,
        showSuggestions,
        suggestions,
        suggestionsTitle,
        threadListAdapter,
        threadSwitcher,
        viewModel,
        assistantIdentity,
    } = useInstructorHugoChat({
        isOpen,
        pathname: location.pathname,
    });
    const chatViewKey = chat.id || chat.local_id;
    const {
        animatedThreadListAdapter,
        clearPendingThreadTransition,
        displayedThreadSwitcher,
        pendingThreadTransition,
        startOpenTransition,
    } = useHugoThreadTransitions({
        chat,
        chatViewKey,
        isChatRouteSynced,
        isThreadSidebarExpanded,
        setIsThreadSidebarExpanded,
        threadListAdapter,
        threadSwitcher,
    });

    const toggleThreadSidebar = useCallback(() => {
        setIsThreadSidebarExpanded((currentValue) => {
            const nextValue = !currentValue;
            if (nextValue) onRefreshThreads();
            return nextValue;
        });
    }, [onRefreshThreads]);
    const isFullScreenLayout = chatLayout === ChatLayout.FULLSCREEN;

    const toggleChatLayout = useCallback(() => {
        setChatLayout((currentValue) =>
            currentValue === ChatLayout.FULLSCREEN ? ChatLayout.DEFAULT : ChatLayout.FULLSCREEN
        );
    }, []);

    useEffect(() => {
        onFullScreenChange(isFullScreenLayout && isPanelVisible);

        return () => onFullScreenChange(false);
    }, [isFullScreenLayout, isPanelVisible, onFullScreenChange]);

    const openChat = useCallback(() => {
        startOpenTransition();
        openPanel();
    }, [openPanel, startOpenTransition]);

    const closeChat = useCallback(() => {
        closePanel();
        setChatLayout(ChatLayout.DEFAULT);
        clearPendingThreadTransition();
        setIsThreadSidebarExpanded(false);
    }, [closePanel, clearPendingThreadTransition]);

    const handleDeleteClick = useCallback(
        (threadId) => {
            if (!threadId) return;

            setDialog({
                open: true,
                message: t('dialog.delete-chat-confirmation.title', 'Permanently Delete This Chat?'),
                description: t(
                    'dialog.delete-chat-confirmation.description',
                    "This will delete the chat and all messages in it. You won't be able to recover it later."
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('controls.delete', 'Delete'),
                cancelButtonText: t('controls.cancel', 'Cancel'),
                onAction: async () => {
                    await onDeleteThread(threadId);
                    closeDialog();
                },
                onCancel: closeDialog,
                onClose: closeDialog,
            });
        },
        [t, setDialog, closeDialog, onDeleteThread]
    );

    const displayViewModel = {
        ...viewModel,
        header: {
            ...viewModel.header,
            title: isThreadSidebarExpanded
                ? t('chat:hugo.conversation-history', 'Conversation History')
                : viewModel.header.title,
        },
    };

    return (
        <>
            {isPanelMounted ? (
                <ClickAwayListener
                    mouseEvent='onMouseDown'
                    touchEvent='onTouchStart'
                    onClickAway={() => {
                        if (!isDialogOpen && isPanelVisible) {
                            closeChat();
                        }
                    }}
                >
                    <HugoPanelFrame
                        chatLayout={chatLayout}
                        isVisible={isPanelVisible}
                        launcherVerticalPosition={launcherVerticalPosition}
                    >
                        <HugoPanelShell direction={'row'}>
                            <ChatPanelRuntime
                                afterHeaderContent={
                                    <HugoThreadSidebar
                                        isExpanded={isThreadSidebarExpanded}
                                        threadSwitcher={displayedThreadSwitcher}
                                        onDeleteThread={handleDeleteClick}
                                    />
                                }
                                buildOutgoingRequest={buildOutgoingRequest}
                                chat={chat}
                                hasOlderMessages={false}
                                headerStartContent={
                                    <Stack direction='row' alignItems='center' spacing={1}>
                                        <ThreadHeaderButton
                                            aria-label={
                                                isThreadSidebarExpanded
                                                    ? t('chat:hugo.close-panel-label', 'Close threads panel')
                                                    : t('chat:hugo.open-panel-label', 'Open threads panel')
                                            }
                                            kind={isThreadSidebarExpanded ? 'chevronLeft' : 'hamburger'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={toggleThreadSidebar}
                                        />
                                    </Stack>
                                }
                                headerEndContent={
                                    <HugoHeaderActions
                                        chatLayout={chatLayout}
                                        onClose={closeChat}
                                        onToggleChatLayout={toggleChatLayout}
                                    />
                                }
                                assistantIdentity={assistantIdentity}
                                hideBackButton
                                isBootstrapping={isBootstrapping}
                                isLoadingOlderMessages={false}
                                isOpen={isOpen}
                                messageLayout={chatLayout}
                                onChatRequestStateChange={onChatRequestStateChange}
                                showSuggestions={showSuggestions}
                                suggestions={suggestions}
                                suggestionsTitle={suggestionsTitle}
                                suggestionsVariant={CompactRightVariant}
                                threadFallback={
                                    pendingThreadTransition ? (
                                        <ThreadTransitionLoading>
                                            <Loading />
                                        </ThreadTransitionLoading>
                                    ) : null
                                }
                                threadListAdapter={animatedThreadListAdapter}
                                viewModel={displayViewModel}
                            />
                        </HugoPanelShell>
                    </HugoPanelFrame>
                </ClickAwayListener>
            ) : (
                <HugoLauncherControl
                    verticalPosition={launcherVerticalPosition}
                    onOpen={openChat}
                    onVerticalPositionChange={setLauncherVerticalPosition}
                />
            )}
        </>
    );
};

const HugoHeaderActions = memo(({ chatLayout, onClose, onToggleChatLayout }) => {
    const { t } = useTranslation();
    const isFullScreenLayout = chatLayout === ChatLayout.FULLSCREEN;

    return (
        <>
            <HeaderActionButton
                aria-label={
                    isFullScreenLayout
                        ? t('chat:hugo.exit-full-screen-label', 'Exit full screen Hugo chat')
                        : t('chat:hugo.open-full-screen-label', 'Open Hugo chat full screen')
                }
                kind={isFullScreenLayout ? 'fullScreenExit' : 'fullScreenEnter'}
                size={'small'}
                variant={'square'}
                onClick={onToggleChatLayout}
            />
            <HeaderActionButton
                aria-label={t('chat:hugo.close-label', 'Close Hugo Assistant')}
                kind={'exit'}
                size={'small'}
                variant={'square'}
                onClick={onClose}
            />
        </>
    );
});

const ThreadTransitionLoading = styled('div')(() => ({
    flex: 1,
    minWidth: 0,
    minHeight: 0,
}));

const ThreadHeaderButton = styled(IconButton)(({ disabled }) => ({
    width: '24px',
    flexShrink: 0,
    opacity: disabled ? 0.35 : 1,
    marginInline: '-8px 4px',
}));

const HeaderActionButton = styled(IconButton)(() => ({
    flexShrink: 0,
}));

export default InstructorHugoChat;
